import { useEffect, useState } from "react";
import { getSkillProfile, type SkillProfile } from "../lib/api";

const DIM_META: Array<{
  key: keyof SkillProfile["dimensions"];
  label: string;
  emoji: string;
  hint: (d: SkillProfile["dimensions"]) => string;
}> = [
  { key: "vocab_range",   label: "Vocabulary range", emoji: "📚",
    hint: (d) => `${d.vocab_range.total_lemmas} / ${d.vocab_range.target_for_level} target lemmas` },
  { key: "accuracy",      label: "Accuracy",         emoji: "🎯",
    hint: (d) => `${d.accuracy.turns_window} turns, ${d.accuracy.error_rate.toFixed(2)} err/turn` },
  { key: "pronunciation", label: "Pronunciation",    emoji: "🎤",
    hint: (d) => d.pronunciation.samples_window === 0 ? "No samples yet" : `${d.pronunciation.samples_window} sample(s) this month` },
  { key: "listening",     label: "Listening",        emoji: "🎧",
    hint: (d) => d.listening.attempts_window === 0 ? "No attempts yet" : `${d.listening.attempts_window} attempt(s) this month` },
  { key: "engagement",    label: "Engagement",       emoji: "🔥",
    hint: (d) => `${d.engagement.sessions_window} sessions, ${d.engagement.user_turns_window} turns / 30d` },
  { key: "unit_progress", label: "Unit completion",  emoji: "📦",
    hint: (d) => `${d.unit_progress.completed} / ${d.unit_progress.available} units at your level` },
];

function barColor(score: number): string {
  if (score >= 75) return "bg-emerald-400";
  if (score >= 50) return "bg-lime-400";
  if (score >= 25) return "bg-amber-400";
  return "bg-rose-400";
}

export function SkillsPanel(): JSX.Element | null {
  const [profile, setProfile] = useState<SkillProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getSkillProfile()
      .then((p) => setProfile(p))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !profile) return null;

  return (
    <section className="rounded-2xl border border-ink-700 bg-ink-900/60 p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xs uppercase tracking-widest text-ink-400">Skills profile</h2>
        <div className="text-[10px] text-ink-500">
          last 30 days {"·"} target {profile.target_level ?? "—"}
        </div>
      </div>
      <div className="space-y-3">
        {DIM_META.map(({ key, label, emoji, hint }) => {
          const dim = profile.dimensions[key];
          return (
            <div key={key}>
              <div className="flex items-center justify-between text-xs">
                <div className="text-ink-200">
                  <span className="mr-1">{emoji}</span>{label}
                </div>
                <div className="font-mono text-ink-300">{dim.score}</div>
              </div>
              <div className="mt-1 h-2 rounded-full bg-ink-800 overflow-hidden">
                <div
                  className={`h-full transition-all ${barColor(dim.score)}`}
                  style={{ width: `${dim.score}%` }}
                />
              </div>
              <div className="mt-0.5 text-[10px] text-ink-500">{hint(profile.dimensions)}</div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
